"use client";

import {Form} from "@/components/ui/form";
import {useForm, useWatch} from "react-hook-form";
import {CWFInput} from "@/components/ui/CWFInput";
import {RiCheckboxCircleFill, RiCloseCircleFill} from "react-icons/ri";
import {useState} from "react";
import CWFDatePicker from "@/components/ui/CWFDatePicker";
import CWFCheckbox from "@/components/ui/CWFCheckbox";
import Link from "next/link";
import CWFRadioGroup from "@/components/ui/CWFRadioGroup";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import CWFButton from "@/components/ui/CWFButton";
import axios from "@/lib/axios";
import toast from "react-hot-toast";
import {signIn, useSession} from "next-auth/react";

function pwdIsLenOk(password){
    return password?.length >= 8;
}

function pwdIsCompOk(password) {
    return password?.match(/.*([0-9]|\W).*/); // /^(?=.*\d)(?=.*[^a-zA-Z0-9]).*$/;
}

function PwdRequirement({ title, valid }) {
    return (
        <li className={`flex items-center gap-2 text-xs ${valid ? "text-green-500" : "text-muted-foreground"} font-semibold`}>
            {valid ? (
                <RiCheckboxCircleFill className="text-green-500 min-w-max" size="15px" />
            ) : (
                <RiCloseCircleFill className="text-muted-foreground min-w-max" size="15px" />
            )}
            {title}
        </li>
    );
}

export default function RegisterForm({ onSuccess }) {

    const [step, setStep] = useState(1);

    return (
        step === 1 ? (
            <Step1 onDone={() => setStep(2)} />
        ) : (
            <Step2 onSuccess={onSuccess} />
        )
    );
}

const REQUIRED_ERROR = "Ce champ est requis.";

const formSchemaStep1 = yup.object({
    email: yup.string().email("Veuillez fournir une adresse email valide.").required(REQUIRED_ERROR),
    password: yup.string()
        .matches(/.*([0-9]|\W).*/, "Votre mot de passe ne respecte pas les prérequis.")
        .required(REQUIRED_ERROR),
    password_confirmation: yup.string()
        .oneOf([yup.ref('password'), null], "La confirmation est différente du mot de passe.")
        .required(REQUIRED_ERROR),
});

function Step1({ onDone }) {

    const formStep1 = useForm({
        resolver: yupResolver(formSchemaStep1),
    });
    const [error, setError] = useState(false);
    const password = useWatch({ control: formStep1.control, name: "password" });
    const [loadingStep1, setLoadingStep1] = useState(false);
    const [showPass, setShowPass] = useState(false);
    function onSubmitStep1(data) {
        setLoadingStep1(true);
        setError(false);
        axios.post('/register', data)
            .then(res => {
                signIn("cwf", { email: data.email, password: data.password, redirect: false })
                    .then(res => {
                        if (!res?.ok) {
                            toast.error("La connexion a échoué.");
                            return;
                        }
                        onDone();
                        setLoadingStep1(false)
                    })
            })
            .catch(() => {
                setError(true);
                toast.error("La création de votre compte a échoué.");
                setLoadingStep1(false);
            });

    }

    return (
        <Form {...formStep1}>
            <form onSubmit={formStep1.handleSubmit(onSubmitStep1)} className="grid gap-4">
                {error && (
                    <p className="rounded-md border border-red-300 bg-red-50 text-red-500 text-center text-sm p-1">
                        Il semblerait que vous ayez déjà un compte avec cette adresse e-mail.
                    </p>
                )}
                <CWFInput control={formStep1.control} name="email" label="Adresse email" type="email"/>

                <div>
                    <CWFInput control={formStep1.control} name="password" label="Mot de passe" type={showPass ? "text" : "password"} withIcon={true} showPass={showPass} setShowPass={setShowPass}/>
                    <ul className="mt-3 px-3 grid gap-2">
                        <PwdRequirement title="Fiabilité du mot de passe" valid={pwdIsCompOk(password) && pwdIsLenOk(password)}/>
                        <PwdRequirement title="Au moins 8 caractères" valid={pwdIsLenOk(password)}/>
                        <PwdRequirement title="Contient un chiffre ou un symbole" valid={pwdIsCompOk(password)}/>

                        {/*<li className="flex items-center gap-2 text-xs text-neutre-700 font-semibold"><RiCloseCircleFill className="text-neutre-800 min-w-max" size="15px" />Ne doit pas contenir votre nom ni votre adresse e-mail</li>*/}
                    </ul>
                </div>

                <CWFInput control={formStep1.control} name="password_confirmation" label="Confirmation du mot de passe"
                          type={showPass ? "text"  : "password"} withIcon={true} setShowPass={setShowPass} showPass={showPass}/>

                <CWFButton type="submit" loading={loadingStep1}>Continuer</CWFButton>
            </form>
        </Form>
    );
}

const formSchemaStep2 = yup.object({
    firstname: yup.string().required(REQUIRED_ERROR),
    lastname: yup.string().required(REQUIRED_ERROR),
    birth_date: yup.date()
        .max(new Date(Date.now()), "La date doit être antérieure à aujourd'hui.")
        .required(REQUIRED_ERROR),
    type: yup.number()
        .oneOf([1, 2], "Veuillez fournir une valeur correcte.")
        .required(REQUIRED_ERROR),
    job: yup.string()
        .when("type", {
            is: 2,
            then: schema =>  schema.required(REQUIRED_ERROR)
        }),
    speciality: yup.string()
        .when("type", {
            is: 2,
            then: schema =>  schema.required(REQUIRED_ERROR)
        }),
    terms: yup.boolean()
        .oneOf([true], "Vous devez accepter nos termes et conditions pour continuer.")
        .required("Vous devez accepter nos termes et conditions pour continuer."),
    //yup.bool().oneOf([true], 'Field must be checked')
});

function Step2({ onSuccess }) {

    const {data: session} = useSession();

    const formStep2 = useForm({
        resolver: yupResolver(formSchemaStep2),
    });
    const type = useWatch({ control: formStep2.control, name: "type" });
    const [loadingStep2, setLoadingStep2] = useState(false);

    function onSubmitStep2(data) {
        setLoadingStep2(true);
        axios.put('/users',
            {...data, birth_date: data.birth_date.toISOString().split('T')[0]},
            {
                headers: { Authorization: `Bearer ${session.user.token}` }
            })
            .then(() => {
                onSuccess?.();
            })
            .catch(() => toast.error("La mise à jour de vos informations a échoué."))
            .finally(() => setLoadingStep2(false));
    }

    return (
        <Form {...formStep2}>
            <form onSubmit={formStep2.handleSubmit(onSubmitStep2)} className="grid grid-cols-2 gap-4">
                <CWFInput control={formStep2.control} name="firstname" label="Prénom"/>
                <CWFInput control={formStep2.control} name="lastname" label="Nom"/>
                <CWFDatePicker
                    control={formStep2.control}
                    modal={true}
                    className="col-span-2"
                    name="birth_date"
                    label="Date de naissance"
                    fromYear={new Date(Date.now()).getFullYear() - 123}
                    toYear={new Date(Date.now()).getFullYear() -18}
                />

                <CWFRadioGroup
                    control={formStep2.control}
                    name="type"
                    label="Vous êtes..."
                    className="col-span-2 gap-3"
                    orientation="horizontal"
                    options={[
                        {label: "Particulier", value: 1},
                        {label: "Professionnel", value: 2},
                    ]}
                />

                {type === 2 && (
                    <>
                        <CWFInput control={formStep2.control} className="col-span-2" name="job" label="Profession"/>
                        <CWFInput control={formStep2.control} className="col-span-2" name="speciality" label="Spécialité"/>
                    </>
                )}

                <CWFCheckbox
                    control={formStep2.control} className="col-span-2" name="terms"
                    label="Vous acceptez les termes et conditions d'utilisation"
                    description={(
                        <span className="text-xs">
                            Vous reconnaissez avoir pris connaissance de
                            nos <Link href={"/politique-de-confidentialité"} className="text-[#2684FF] font-semibold hover:underline">conditions d&apos;utilisations</Link> et
                            nos <Link href={"/politique-de-confidentialité"} className="text-[#2684FF] font-semibold hover:underline">conditions générales de ventes</Link>
                        </span>
                    )}
                />

                <CWFButton type="submit" className="col-span-2" loading={loadingStep2}>M&apos;inscrire</CWFButton>
            </form>
        </Form>
    );
}
